import React, { useLayoutEffect, useRef, useState } from "react";
import EditableText from "../Offers/proposal/EditableText";
import ImageTools from "../Offers/proposal/ImageTools";
import { useAssetUrl } from "../Offers/proposal/assets";
import { EmbedsContext, TwinView } from "../Offers/proposal/Twin";
import { C, DISPLAY, FONT, LOGO, LOGO_RATIO, longDate } from "../Offers/proposal/theme";
import { updateItem } from "../Offers/proposal/proposalOps";
import { captionNumbers, sectionNumbers } from "./reportOps";

// Charte des rapports : celle des présentations (bleu #3C83F6, titres Montserrat noirs, étiquettes en capitales
// bleues, encarts bleu pâle, couverture claire avec filet bleu et bande pâle), mise en page A4 portrait.
export const PAGE = { width: 794, height: 1123, top: 83, bottom: 83, side: 72 }; // px à 96 dpi (22 mm, 19 mm)
const BODY = { fontFamily: FONT, fontSize: 13.5, lineHeight: 1.55, color: C.body };
const LABEL = { fontFamily: DISPLAY, fontSize: 11, fontWeight: 700, letterSpacing: 1.6, textTransform: "uppercase", color: C.blue };
const CAPTION = { fontSize: 11.5, color: C.muted, lineHeight: 1.4 };
// Repère invisible d'un titre (premier passage d'impression : page de chaque section pour le sommaire). Dans le flux
// du titre : en position absolue, Chrome le peignait sur la page précédente quand le titre passait à la suivante.
const MARKER = { fontSize: 1, lineHeight: 0, color: "#ffffff", letterSpacing: 0 };
// À l'écran, les pages A4 sont empilées avec un intervalle gris.
const GAP = 24;
const SPAN = PAGE.height + GAP;
const ROOM = PAGE.height - PAGE.top - PAGE.bottom;
const CHROME = { position: "absolute", fontFamily: "Arial, sans-serif", fontSize: 10, color: C.muted, whiteSpace: "nowrap",
  overflow: "hidden", textOverflow: "ellipsis", maxWidth: "48%" };

const Image = ({ asset, alt, style }) => {
  const url = useAssetUrl(asset);
  return url ? <img src={url} alt={alt || ""} style={{ display: "block", ...style }} /> : null;
};

// ------------------------------------------------------------------------ couverture
// Champs facultatifs vides (sous-titre, auteurs) : montrés seulement quand on édite la couverture (survol ou saisie),
// pour relire le rapport sans repères « à remplir ».
export const Cover = ({ meta, title, clientName, edit, onMeta, onTitle, print }) => {
  const set = (field) => (value) => onMeta({ [field]: value }, `meta.${field}`);
  const [active, setActive] = useState(false);
  const blank = edit && active;
  const height = print ? "297mm" : PAGE.height;
  const facts = [longDate(meta.date), meta.version && `Version ${meta.version}`, meta.reference && `Réf. ${meta.reference}`]
    .filter(Boolean).join("  •  ");
  return (
    <div style={{ position: "relative", width: print ? "210mm" : PAGE.width, height, background: C.white, overflow: "hidden",
      ...(print ? { breakAfter: "page" } : {}) }}
      onMouseEnter={edit ? () => setActive(true) : undefined} onFocus={edit ? () => setActive(true) : undefined}
      onMouseLeave={edit ? (event) => { if (!event.currentTarget.contains(document.activeElement)) setActive(false); } : undefined}
      onBlur={edit ? (event) => { if (!event.currentTarget.contains(event.relatedTarget)) setActive(false); } : undefined}>
      <img src={LOGO} alt="Logo Innowide" style={{ position: "absolute", left: PAGE.side, top: 64, height: 40, width: 40 * LOGO_RATIO }} />
      {meta.client_logo && (
        <Image asset={meta.client_logo} alt="Logo client"
          style={{ position: "absolute", right: PAGE.side, top: 54, maxWidth: 200, maxHeight: 64, objectFit: "contain" }} />
      )}
      <div style={{ position: "absolute", left: PAGE.side - 22, right: PAGE.side, top: 360, borderLeft: `6px solid ${C.blue}`,
        paddingLeft: 22, display: "flex", flexDirection: "column", gap: 18 }}>
        <EditableText value={meta.kicker} onChange={set("kicker")} editable={edit} singleLine rich={false}
          placeholder="Rapport" label="Nature du document"
          style={{ ...LABEL, fontSize: 15, letterSpacing: 3, lineHeight: "24px" }} />
        <EditableText value={title} onChange={onTitle} editable={edit && Boolean(onTitle)} singleLine rich={false}
          placeholder="Titre du rapport" label="Titre du rapport"
          style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 32, lineHeight: 1.2, color: C.black }} />
        {(meta.subtitle || blank) && (
          <EditableText value={meta.subtitle} onChange={set("subtitle")} editable={edit} rich={false}
            placeholder="Ce que le rapport établit, en une phrase" label="Sous-titre"
            style={{ fontFamily: FONT, fontSize: 18, lineHeight: 1.35, color: C.muted }} />
        )}
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 190, background: C.pale }}>
        <div style={{ position: "absolute", left: PAGE.side, right: PAGE.side, top: 44, display: "flex", flexDirection: "column", gap: 10,
          fontFamily: FONT, fontSize: 14, color: C.muted }}>
          {clientName && <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 15, color: C.black }}>{clientName}</div>}
          {facts && <div>{facts}</div>}
          {(meta.authors || blank) && (
            <EditableText value={meta.authors} onChange={set("authors")} editable={edit} singleLine rich={false}
              placeholder="Auteurs" label="Auteurs" style={{ fontSize: 14, color: C.muted }} />
          )}
          {meta.confidential && <div style={{ ...LABEL, fontSize: 10, color: C.muted }}>Document confidentiel</div>}
        </div>
      </div>
    </div>
  );
};

// En-tête et pied d'une page à l'écran, comme les marges de page de l'impression (printCss).
const PageChrome = ({ top, number, total, meta, title, clientName }) => {
  const footer = [title, meta.confidential ? "Confidentiel" : "", "innowide.fr"].filter(Boolean).join(" · ");
  return (
    <>
      <span style={{ ...CHROME, top: top + PAGE.top - 30, left: PAGE.side, fontWeight: 700, color: C.blue, letterSpacing: 1.6 }}>
        {String(meta.kicker || "Rapport").toUpperCase()}
      </span>
      {clientName && <span style={{ ...CHROME, top: top + PAGE.top - 30, right: PAGE.side }}>{clientName}</span>}
      <span style={{ ...CHROME, top: top + PAGE.height - PAGE.bottom + 18, left: PAGE.side, maxWidth: "75%" }}>{footer}</span>
      <span style={{ ...CHROME, top: top + PAGE.height - PAGE.bottom + 18, right: PAGE.side }}>{number} / {total}</span>
    </>
  );
};

// Mise en page de l'écran : chaque titre ou bloc qui passerait sur la marge du bas est repoussé en haut de la page
// suivante (un titre part avec son premier bloc ; « nouvelle page » force le saut). Les positions sont relevées sans
// les décalages déjà appliqués, puis les décalages recalculés : la mise en page suit la saisie et les images.
// Approximation du PDF : Chrome y coupe en plus un long texte entre deux pages, l'écran le repousse en entier.
const usePagination = (enabled) => {
  const ref = useRef(null);
  const [layout, setLayout] = useState({ push: {}, page: {}, count: 1 });
  // Garde-fou : un bloc pile à la limite d'une page pourrait sauter d'une page à l'autre à chaque passage.
  const churn = useRef({ at: 0, count: 0 });
  useLayoutEffect(() => {
    const node = ref.current;
    if (!enabled || !node) return undefined;
    const measure = () => {
      const box = node.getBoundingClientRect();
      const ratio = box.width / PAGE.width || 1;
      let applied = 0;
      const units = [...node.querySelectorAll("[data-unit]")].map((element) => {
        const rect = element.getBoundingClientRect();
        applied += Number(element.dataset.push || 0);
        return { id: element.dataset.unit, section: element.dataset.section, heading: element.dataset.kind === "heading",
          force: element.dataset.force === "1", top: (rect.top - box.top) / ratio - applied, height: rect.height / ratio };
      });
      const push = {};
      const page = {};
      let extra = 0;
      let bottom = 0;
      units.forEach((unit, index) => {
        let top = unit.top + extra;
        const current = Math.floor(top / SPAN);
        const next = units[index + 1];
        const height = unit.heading && next && next.section === unit.section ? next.top + next.height - unit.top : unit.height;
        const limit = current * SPAN + PAGE.height - PAGE.bottom;
        const busy = bottom > current * SPAN + PAGE.top + 1;
        if ((unit.force && busy) || top >= limit || (top + height > limit && height <= ROOM)) {
          const move = Math.round((current + 1) * SPAN + PAGE.top - top);
          push[unit.id] = move;
          extra += move;
          top += move;
        }
        page[unit.id] = Math.floor(top / SPAN);
        bottom = Math.max(bottom, top + unit.height);
      });
      const count = Math.max(1, Math.floor(bottom / SPAN) + 1);
      // Zoomée, la page s'aligne sur les pixels de l'écran : un décalage relu peut varier de quelques px d'un passage
      // à l'autre. Sans cette tolérance, deux mises en page presque identiques alterneraient sans fin.
      const tolerance = Math.max(3, 3 / ratio);
      setLayout((previous) => {
        const keys = new Set([...Object.keys(previous.push), ...Object.keys(push)]);
        const same = previous.count === count
          && [...keys].every((key) => Math.abs((previous.push[key] || 0) - (push[key] || 0)) <= tolerance)
          && Object.keys(page).every((key) => previous.page[key] === page[key]);
        if (same) return previous;
        const now = Date.now();
        if (now - churn.current.at > 1000) churn.current = { at: now, count: 0 };
        churn.current.count += 1;
        return churn.current.count > 8 ? previous : { push, page, count };
      });
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(node.lastElementChild || node);
    return () => observer?.disconnect();
  });
  return [ref, layout];
};

// -------------------------------------------------------------------------- sommaire
export const Contents = ({ sections, numbers, pages, print }) => {
  const visible = sections.filter((section) => !section.hidden);
  return (
    <div style={{ ...BODY, ...(print ? { breakAfter: "page" } : { padding: `${PAGE.top}px ${PAGE.side}px` }) }}>
      <h1 style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 24, color: C.black, margin: "0 0 26px",
        paddingBottom: 8, borderBottom: `2px solid ${C.line}` }}>Sommaire</h1>
      {visible.map((section) => (
        <div key={section.id} style={{ display: "flex", alignItems: "baseline", gap: 10,
          margin: section.level === 2 ? "4px 0 4px 34px" : "12px 0 4px",
          fontFamily: section.level === 2 ? FONT : DISPLAY, fontWeight: section.level === 2 ? 400 : 700,
          fontSize: section.level === 2 ? 13 : 14, color: section.level === 2 ? C.body : C.black }}>
          <span style={{ color: C.blue, minWidth: section.level === 2 ? 30 : 24 }}>{numbers[section.id]}</span>
          <span>{section.title || "Sans titre"}</span>
          <span style={{ flex: 1, borderBottom: `1px dotted ${C.line}`, transform: "translateY(-4px)" }} />
          <span style={{ minWidth: 20, textAlign: "right", color: C.muted }}>{pages?.[section.id] ?? ""}</span>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------- blocs
const Paragraphs = ({ text, edit, onChange, style, placeholder, label }) => (
  <EditableText value={text} onChange={onChange} editable={edit} placeholder={placeholder} label={label} style={style} />
);

// Les champs facultatifs vides (intertitre, légende, précision) ne s'affichent que sur le bloc sélectionné.
const TextBlock = ({ block, edit, set, selected }) => {
  const box = block.style === "box";
  const note = block.style === "note";
  return (
    <div style={box ? { background: C.pale, borderLeft: `4px solid ${C.blue}`, borderRadius: 6, padding: "12px 16px" } : undefined}>
      {(block.label || (edit && selected)) && (
        <EditableText value={block.label} onChange={set("label")} editable={edit} singleLine rich={false}
          placeholder="Intertitre (facultatif)" label="Intertitre"
          style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 13.5, color: C.black, marginBottom: 4 }} />
      )}
      <Paragraphs text={block.text} onChange={set("text")} edit={edit} label="Texte" placeholder="Texte du paragraphe"
        style={note ? { ...BODY, fontSize: 11.5, color: C.muted } : BODY} />
    </div>
  );
};

const Caption = ({ number, text, edit, onChange, align = "center", blank = edit }) => (
  <div style={{ ...CAPTION, textAlign: align, marginTop: 6, display: "flex", gap: 4, justifyContent: align === "center" ? "center" : "flex-start" }}>
    <span style={{ fontWeight: 700, color: C.body, whiteSpace: "nowrap" }}>{number}{(text || blank) ? " –" : ""}</span>
    {(text || blank) && (
      <EditableText value={text} onChange={onChange} editable={edit} singleLine rich={false} placeholder="Légende"
        label="Légende" style={CAPTION} />
    )}
  </div>
);

// Image sélectionnée dans l'éditeur : fond pro, retouche décrite, recadrage. Le résultat remplace l'image si elle n'a pas
// changé entre-temps.
const ImageBlock = ({ block, edit, set, number, selected, onBlock }) => (
  <figure style={{ margin: 0, breakInside: "avoid" }}>
    {block.image ? (
      <div style={{ position: "relative", width: `${block.width || 100}%`, margin: "0 auto" }}>
        <Image asset={block.image} alt={block.caption} style={{ width: "100%", height: "auto" }} />
        {edit && selected && (
          <ImageTools asset={block.image}
            onReplace={(ref) => onBlock((current) => (current.image === block.image ? { ...current, image: ref } : current))} />
        )}
      </div>
    ) : (
      <div style={{ height: 180, background: C.pale, border: `1.5px dashed ${C.line}`, borderRadius: 8, display: "flex",
        alignItems: "center", justifyContent: "center", ...LABEL }}>Figure à insérer</div>
    )}
    <Caption number={number} text={block.caption} edit={edit} blank={edit && selected} onChange={set("caption")} />
  </figure>
);

const cell = { border: `1px solid ${C.line}`, padding: "6px 9px", verticalAlign: "top", textAlign: "left" };
const TableBlock = ({ block, edit, onBlock, number, selected }) => {
  const setCell = (row, column) => (value) => onBlock((current) => ({
    ...current, rows: current.rows.map((cells, r) => (r === row ? cells.map((c, k) => (k === column ? value : c)) : cells)),
  }), `${block.id}.${row}.${column}`);
  return (
    <div style={{ breakInside: "avoid" }}>
      <Caption number={number} text={block.caption} edit={edit} blank={edit && selected} onChange={(value) => onBlock({ caption: value }, `${block.id}.caption`)} align="left" />
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT, fontSize: 12.5, lineHeight: 1.4, marginTop: 6 }}>
        <tbody>
          {block.rows.map((row, r) => (
            <tr key={r} style={r === 0 ? { background: C.blue, color: C.white } : { background: r % 2 ? C.white : C.pale, color: C.body }}>
              {row.map((value, k) => (
                <td key={k} style={{ ...cell, fontWeight: r === 0 ? 700 : 400 }}>
                  <EditableText value={value} onChange={setCell(r, k)} editable={edit} singleLine rich={false} label="Cellule"
                    style={{ fontSize: 12.5, color: r === 0 ? C.white : C.body }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const FiguresBlock = ({ block, edit, onBlock, selected }) => {
  const setItem = (index, field) => (value) => onBlock((current) => updateItem(current, index, { [field]: value }),
    `${block.id}.${index}.${field}`);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${block.items.length}, 1fr)`, gap: 12, breakInside: "avoid" }}>
      {block.items.map((item, index) => (
        <div key={index} style={{ background: C.pale, borderRadius: 8, padding: "12px 14px", borderTop: `3px solid ${C.blue}` }}>
          <EditableText value={item.value} onChange={setItem(index, "value")} editable={edit} singleLine rich={false}
            placeholder="0,1 mm" label="Chiffre" style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 24, color: C.blue, lineHeight: 1.2 }} />
          <EditableText value={item.label} onChange={setItem(index, "label")} editable={edit} singleLine rich={false}
            placeholder="Ce qu'il mesure" label="Libellé" style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 12, color: C.black, marginTop: 4 }} />
          {(item.detail || (edit && selected)) && (
            <EditableText value={item.detail} onChange={setItem(index, "detail")} editable={edit} rich={false}
              placeholder="Condition, comparaison" label="Précision" style={{ fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 1.35 }} />
          )}
        </div>
      ))}
    </div>
  );
};

const TwinBlock = ({ block, edit, set, number, selected }) => (
  <figure style={{ margin: 0, breakInside: "avoid" }}>
    <TwinView embed={block.embed} poster={block.poster} style={{ height: block.height || 320 }} />
    <Caption number={number} text={block.caption} edit={edit} blank={edit && selected} onChange={set("caption")} />
  </figure>
);

const BLOCKS = { text: TextBlock, image: ImageBlock, table: TableBlock, figures: FiguresBlock, twin: TwinBlock };

// --------------------------------------------------------------------------- sections
const Heading = ({ section, number, edit, onSection, marker, push = 0, unit = false }) => {
  const main = section.level !== 2;
  return (
    <div data-unit={unit ? `h:${section.id}` : undefined} data-kind="heading" data-section={section.id}
      data-force={section.page_break ? "1" : undefined} data-push={push || undefined}
      style={{ position: "relative", display: "flex", gap: main ? 12 : 9, alignItems: "baseline", breakAfter: "avoid",
        margin: main ? "30px 0 14px" : "20px 0 8px", marginTop: (main ? 30 : 20) + push,
        ...(main ? { paddingBottom: 7, borderBottom: `2px solid ${C.line}` } : {}) }}>
      <span style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: main ? 21 : 15, color: C.blue }}>
        {marker && <span style={MARKER}>{`§§${section.id}§§`}</span>}
        {section.annex && main ? `Annexe ${number}` : number}
      </span>
      <div style={{ flex: 1 }}>
        <EditableText value={section.title} onChange={(value) => onSection({ title: value }, "title")} editable={edit}
          singleLine rich={false} placeholder="Titre de la section" label="Titre de la section"
          style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: main ? 21 : 15, color: C.black, lineHeight: 1.25 }} />
      </div>
    </div>
  );
};

// Le rapport entier : couverture, sommaire, sections. mode « print » : pages A4 de Chrome (en-têtes et pieds dans
// les marges de page, voir printCss) ; à l'écran, les mêmes pages A4 (usePagination), éditables sur place si `edit`.
const ReportDocument = ({
  content, title, clientName, embeds = {}, live = false, print = false, markers = false, pages = null,
  edit = false, selection = {}, onSelect = () => {}, onSection = () => {}, onBlock = () => {}, onMeta = () => {},
  onTitle, highlight = null, sectionRef,
}) => {
  const sections = content.sections || [];
  const numbers = sectionNumbers(sections);
  const captions = captionNumbers(sections);
  const shownSections = print ? sections.filter((section) => !section.hidden) : sections;
  const [bodyRef, layout] = usePagination(!print);
  const paper = { background: C.white, boxShadow: "0 1px 4px rgba(15,23,42,.15)" };
  const sheet = print ? {} : { ...paper, position: "relative", width: PAGE.width, height: PAGE.height, margin: `0 auto ${GAP}px`, overflow: "hidden" };
  // Numéros de page de l'écran : couverture 1, sommaire 2, puis le corps (le PDF recalcule les siens).
  const first = content.meta.toc ? 3 : 2;
  const chrome = { meta: content.meta, title, clientName, total: first - 1 + layout.count };
  const screenPages = print ? null : Object.fromEntries(sections.map((section) => [section.id,
    layout.page[`h:${section.id}`] === undefined ? "" : first + layout.page[`h:${section.id}`]]));
  return (
    <EmbedsContext.Provider value={{ embeds, live }}>
      <div style={sheet}>
        <Cover meta={content.meta} title={title} clientName={clientName} edit={edit} onMeta={onMeta} onTitle={onTitle} print={print} />
      </div>
      {content.meta.toc && (
        <div style={sheet}>
          <Contents sections={sections} numbers={numbers} pages={pages || screenPages} print={print} />
          {!print && <PageChrome top={0} number={2} {...chrome} />}
        </div>
      )}
      <div ref={bodyRef} style={print ? {} : { position: "relative", width: PAGE.width, margin: "0 auto", height: layout.count * SPAN - GAP }}>
        {!print && Array.from({ length: layout.count }, (_, index) => (
          <React.Fragment key={index}>
            <div style={{ ...paper, position: "absolute", left: 0, right: 0, top: index * SPAN, height: PAGE.height }} />
            <PageChrome top={index * SPAN} number={first + index} {...chrome} />
          </React.Fragment>
        ))}
      <div style={print ? {} : { position: "relative", padding: `${PAGE.top}px ${PAGE.side}px 0` }}>
        {shownSections.map((section) => {
          const selected = edit && selection.sectionId === section.id && !selection.blockId;
          return (
            <section key={section.id} ref={(node) => sectionRef?.(section.id, node)} data-section-id={section.id}
              onClick={edit ? (event) => { event.stopPropagation(); onSelect(section.id, null); } : undefined}
              style={{ position: "relative", opacity: section.hidden ? 0.4 : 1,
                ...(section.page_break && print ? { breakBefore: "page" } : {}),
                ...(selected ? { outline: `2px solid ${C.line}`, outlineOffset: 8, borderRadius: 4 } : {}) }}>
              <Heading section={section} number={numbers[section.id] || ""} edit={edit} marker={markers} unit={!print}
                push={layout.push[`h:${section.id}`] || 0}
                onSection={(patch, key) => onSection(section.id, patch, key)} />
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {(section.blocks || []).map((block) => {
                  const Block = BLOCKS[block.type];
                  const blockSelected = edit && selection.blockId === block.id;
                  const changed = highlight?.[section.id]?.blocks?.includes(block.id);
                  const patch = (value, key) => onBlock(section.id, block.id, value, key);
                  const push = print ? 0 : layout.push[`b:${block.id}`] || 0;
                  return (
                    <div key={block.id} data-block-id={block.id} data-unit={print ? undefined : `b:${block.id}`}
                      data-section={section.id} data-push={push || undefined}
                      className={[edit && "offer-block", changed && "offer-changed"].filter(Boolean).join(" ") || undefined}
                      data-selected={blockSelected || undefined}
                      onClick={edit ? (event) => { event.stopPropagation(); onSelect(section.id, block.id); } : undefined}
                      style={{ position: "relative", marginTop: push || undefined }}>
                      {Block && (
                        <Block block={block} edit={edit} number={captions[block.id]} onBlock={patch} selected={blockSelected}
                          set={(field) => (value) => patch({ [field]: value }, `${block.id}.${field}`)} />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
      </div>
    </EmbedsContext.Provider>
  );
};

const cssString = (value) => JSON.stringify(String(value || "").replace(/\s+/g, " "));

// Pages A4 de l'impression : couverture sans marges, puis en-tête (nature du document, client) et pied (titre,
// confidentialité, page / total) dans les marges de page (Chromium ≥ 131).
export const printCss = ({ title, kicker, clientName, confidential }) => `
@page { size: A4; margin: 22mm 19mm 22mm 19mm;
  @top-left { content: ${cssString(String(kicker || "Rapport").toUpperCase())}; font: 700 7.5pt Arial, sans-serif; color: ${C.blue}; letter-spacing: 1.2pt; vertical-align: bottom; padding-bottom: 4mm; }
  @top-right { content: ${cssString(clientName)}; font: 7.5pt Arial, sans-serif; color: ${C.muted}; vertical-align: bottom; padding-bottom: 4mm; }
  @bottom-left { content: ${cssString([title, confidential ? "Confidentiel" : "", "innowide.fr"].filter(Boolean).join(" · "))}; font: 7.5pt Arial, sans-serif; color: ${C.muted}; vertical-align: top; padding-top: 4mm; }
  @bottom-right { content: counter(page) " / " counter(pages); font: 7.5pt Arial, sans-serif; color: ${C.muted}; vertical-align: top; padding-top: 4mm; }
}
@page :first { margin: 0; @top-left { content: none; } @top-right { content: none; } @bottom-left { content: none; } @bottom-right { content: none; } }
html, body { margin: 0; padding: 0; background: #ffffff; }
* { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
p, li { orphans: 3; widows: 3; }
`;

export default ReportDocument;
